import type { secretStoreFr } from './secret-store.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts of the “Secret store” screen (ADR 0095). Same keys as `secret-store.fr.ts`. */
export const secretStoreEn: DeepStrings<typeof secretStoreFr> = {
  title: 'Secret store',
  subtitle:
    'Where the values of your organisation’s secrets live: encrypted in the database by default, or in the vault you choose — Azure Key Vault, HashiCorp Vault or AWS Secrets Manager.',
  providers: {
    database: 'Encrypted store in the database (default)',
    'azure-key-vault': 'Azure Key Vault',
    'hashicorp-vault': 'HashiCorp Vault (KV v2)',
    'aws-secrets-manager': 'AWS Secrets Manager',
  },
  writes: {
    external: 'In the external store',
    database: 'In the database, encrypted',
  },
  auths: {
    'client-secret': 'Service principal (client secret)',
    'managed-identity': 'Managed identity of the platform',
    token: 'Token',
    approle: 'AppRole',
    'access-key': 'Access key',
    'default-chain': 'Platform identity (default chain)',
  },
  keys: {
    vault_url: 'Vault URL',
    tenant_id: 'Directory (tenant ID)',
    client_id: 'Application (client ID)',
    client_secret: 'Client secret',
    name_prefix: 'Name prefix',
    address: 'Vault server address',
    mount: 'KV v2 mount',
    path_prefix: 'Path prefix',
    namespace: 'Namespace (Vault Enterprise)',
    approle_mount: 'AppRole mount',
    role_id: 'Role ID',
    token: 'Vault token',
    secret_id: 'Secret ID',
    region: 'Region',
    access_key_id: 'Access key ID',
    secret_access_key: 'Secret access key',
  },
  summary: {
    title: 'Current state',
    provider: 'Store',
    writes: 'New values',
    inDatabase: 'Values in the database',
    inExternal: 'Values in the external store',
    parameters: 'Connection parameters',
    credentials: 'Credentials set',
    noCredentials: 'None (platform identity)',
    credentialSet: 'set — never shown again',
    readsByForm:
      'A value is always read where it lives: changing where new values are written moves nothing. To move existing values, run the migration.',
  },
  check: {
    action: 'Check the connection',
    ok: 'The store answers: a probe value was written then read back ({{duration}} ms).',
    failed: 'The store does not answer as expected.',
  },
  migration: {
    toExternal_one: 'Move {{count}} value to the external store',
    toExternal_other: 'Move {{count}} values to the external store',
    toDatabase_one: 'Bring {{count}} value back to the database',
    toDatabase_other: 'Bring {{count}} values back to the database',
    title: {
      external: 'Migration to the external store',
      database: 'Back to the database',
    },
    counts:
      '{{moved}} moved, {{skipped}} skipped (already in place or changed meanwhile), {{failed}} failed.',
    done: {
      external: 'Migration to the external store finished.',
      database: 'Back to the database finished.',
    },
  },
  remove: {
    action: 'Remove the external store',
    title: 'Remove the external store?',
    confirm:
      'The organisation goes back to the encrypted store in the database and its connection credentials are forgotten. Refused while a value still lives in the external store.',
    done: 'External store removed: secrets are encrypted in the database again.',
    inUse_one:
      '{{count}} value lives in the external store: bring it back to the database before removing or changing the store.',
    inUse_other:
      '{{count}} values live in the external store: bring them back to the database before removing or changing the store.',
  },
  form: {
    createTitle: 'Designate an external store',
    editTitle: 'Change the external store',
    help: 'The connection is checked before it is saved: a store that does not answer is never saved. Credentials are sealed in the database and never shown again.',
    submit: 'Check and save',
    saved: 'Store checked and saved.',
    noProvider: 'No external store is allowed on this platform.',
  },
  fields: {
    provider: 'Provider',
    providerHelp: 'Only the providers allowed by the platform operator are offered.',
    writes: 'Write new values',
    writesHelp:
      '“In the database” stops writing to the external store without moving anything: the first step back.',
    auth: 'Authentication',
    credentialHelp: 'Entered once, sealed in the database, never shown again.',
    credentialKept: 'Already set: leave empty to keep it, type a value to replace it.',
  },
  codes: {
    SECRET_STORE_INVALID: 'Invalid store parameters: check the URL, the address or the region.',
    SECRET_STORE_IN_USE:
      'Values still live in this store: bring them back to the database before removing it or changing provider or store.',
    SECRET_STORE_NOT_CONFIGURED: 'No external store is designated for this organisation.',
    SECRET_STORE_NOT_FOUND: 'No external store is designated for this organisation.',
    SECRET_STORE_UNAVAILABLE:
      'The external store cannot be reached (network, timeout or service error): retry later or check that the platform can reach it.',
    SECRET_STORE_DENIED:
      'The external store denies access: invalid credentials or missing rights (read, write and delete secrets).',
    SECRET_STORE_VALUE_MISSING:
      'A referenced value no longer exists in the external store: enter the secret again.',
    feature_disabled:
      'This provider, or the platform identity, is not allowed by the operator of this installation.',
  },
};
